
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import type { User } from "../UserRoleTable";

export type SortField = 'email' | 'usage';

export function useUsers() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<SortField>('usage');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [showMultiRoleOnly, setShowMultiRoleOnly] = useState(false);
  
  const usersPerPage = 10;
  
  // Toggle sort order function
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Set sort field
  const setSortBy = (field: SortField) => {
    if (field === sortField) {
      toggleSortOrder();
    } else {
      setSortField(field);
      setSortOrder(field === 'usage' ? 'desc' : 'asc');
    }
  };

  // Fetch ALL users (we have ~120, so this is fine)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch ALL users without pagination
        const { data, error } = await supabase
          .rpc("get_users_with_roles")
          .order('email', { ascending: true });

        if (error) {
          console.error("Error fetching users:", error);
          setError("Failed to load users. Please try again later.");
          throw error;
        }

        if (!data) {
          setUsers([]);
          return;
        }

        // Fetch usage metrics for all users
        const { data: usageData } = await supabase
          .from('feature_metrics')
          .select('user_id, usage_count');

        // Aggregate usage per user
        const usageByUser: Record<string, number> = {};
        if (usageData) {
          usageData.forEach((metric: any) => {
            if (metric.user_id) {
              usageByUser[metric.user_id] = (usageByUser[metric.user_id] || 0) + (metric.usage_count || 0);
            }
          });
        }

        // Process the roles from JSONB array (Postgres returns this as a JS array)
        const processedUsers = data.map((user: any) => ({
          ...user,
          roles: Array.isArray(user.roles) ? user.roles.filter(r => r) : [],
          usage_count: usageByUser[user.id] || 0
        }));

        // Fetch specialist assignments for caregivers
        const caregiverIds = processedUsers
          .filter((u: any) => u.roles.includes('caregiver'))
          .map((u: any) => u.id);

        let specialistAssignments: Record<string, { specialist_name: string; specialist_id: string; caregiver_id: string }> = {};

        if (caregiverIds.length > 0) {
          const { data: assignments } = await supabase
            .from('specialist_caregiver')
            .select(`
              caregiver_id,
              specialist_id,
              caregivers!inner(auth_user_id, id),
              specialists!inner(name)
            `)
            .eq('archived', false)
            .in('caregivers.auth_user_id', caregiverIds);

          if (assignments) {
            specialistAssignments = assignments.reduce((acc: any, assignment: any) => {
              acc[assignment.caregivers.auth_user_id] = {
                specialist_name: assignment.specialists.name,
                specialist_id: assignment.specialist_id,
                caregiver_id: assignment.caregivers.id,
              };
              return acc;
            }, {});
          }
        }

        // Add specialist info to users
        const usersWithSpecialists = processedUsers.map((user: any) => ({
          ...user,
          specialist_name: specialistAssignments[user.id]?.specialist_name,
          specialist_id: specialistAssignments[user.id]?.specialist_id,
          caregiver_id: specialistAssignments[user.id]?.caregiver_id,
        }));

        console.log("Processed users sample:", usersWithSpecialists.slice(0, 3));
        setUsers(usersWithSpecialists);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to load users");
        toast({
          title: "Error",
          description: "Failed to load users",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search query and role filters, then paginate
  useEffect(() => {
    let filtered = [...users];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(query) || 
        (user.user_name && user.user_name.toLowerCase().includes(query)) ||
        user.roles.some(role => role.toLowerCase().includes(query))
      );
    }
    
    // Role filter
    if (roleFilter.length > 0) {
      filtered = filtered.filter(user => {
        return roleFilter.some(filter => user.roles.includes(filter));
      });
    }
    
    // Multi-role filter
    if (showMultiRoleOnly) {
      filtered = filtered.filter(user => user.roles.length >= 2);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortField === 'usage') {
        const aUsage = (a as any).usage_count || 0;
        const bUsage = (b as any).usage_count || 0;
        return sortOrder === 'desc' ? bUsage - aUsage : aUsage - bUsage;
      } else {
        const aEmail = a.email.toLowerCase();
        const bEmail = b.email.toLowerCase();
        return sortOrder === 'asc' 
          ? aEmail.localeCompare(bEmail) 
          : bEmail.localeCompare(aEmail);
      }
    });
    
    // Update total pages based on filtered results
    setTotalPages(Math.ceil(filtered.length / usersPerPage));
    
    // Client-side pagination
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    setFilteredUsers(paginated);
  }, [searchQuery, users, roleFilter, showMultiRoleOnly, currentPage, sortField, sortOrder]);

  // Reset to first page when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, showMultiRoleOnly]);

  return {
    users,
    filteredUsers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    currentPage,
    totalPages,
    setCurrentPage,
    sortOrder,
    toggleSortOrder,
    sortField,
    setSortBy,
    roleFilter,
    setRoleFilter,
    showMultiRoleOnly,
    setShowMultiRoleOnly
  };
}
